import React, { useEffect, useState } from 'react'
import DashboardLayout from '../../components/Admin/DashboardLayout'
import {
  MDBBtn,
  MDBContainer,
  MDBRow,
  MDBCol,
  MDBModal,
  MDBModalHeader,
  MDBModalBody,
  MDBModalDialog,
  MDBModalContent,
  MDBModalTitle,
} from 'mdb-react-ui-kit'
import PageHeading from '../../components/shared/PageHeading.component'
import YearCalendar from './components/YearCalendar/YearCalendar.component'
import HolidayStore from './store/Holiday.store'
import moment from 'moment'

import './HolidaysPage.scss'
import { cleanHTML } from '../../shared/formatters/cleanHTML'

/**
 * Process HTML description for safe rendering with working links.
 * Adds target="_blank" to links (except mailto) and fixes URLs missing protocols.
 */
const processDescriptionHtml = html => {
  if (!html || typeof html !== 'string') return ''
  // Clean escaped quotes first
  let processed = cleanHTML(html)
  // Fix href URLs that start with www. but lack a protocol
  processed = processed.replace(/href=["']?(www\.)/gi, 'href="http://www.')
  // Add target="_blank" to links that are not mailto:
  processed = processed.replace(/<a(?!.*?mailto:)/g, '<a target="_blank"')
  return processed
}

/**
 * HolidayDetailModal Component
 *
 * Modal to display details of holidays on a selected date.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Callback to close the modal
 * @param {string} props.date - Selected date string
 * @param {Array} props.holidays - Array of holiday objects for the date
 */
const HolidayDetailModal = ({ isOpen, onClose, date, holidays }) => {
  if (!holidays || holidays.length === 0) return null

  const formattedDate = date ? moment(date).format('MMMM D, YYYY') : ''

  return (
    <MDBModal open={isOpen} onClose={onClose} centered>
      <MDBModalDialog>
        <MDBModalContent>
          <MDBModalHeader>
            <MDBModalTitle>Holidays on {formattedDate}</MDBModalTitle>
            <MDBBtn type='button' className='btn-close' color='none' onClick={onClose}></MDBBtn>
          </MDBModalHeader>
          <MDBModalBody>
            <ul className='holiday-detail-list'>
              {holidays.map((holiday, idx) => (
                <li key={`detail-${holiday.id}-${idx}`} className='holiday-detail-item'>
                  <strong>{holiday.summary}</strong>
                  {holiday.description && (
                    <div
                      className='holiday-description'
                      dangerouslySetInnerHTML={{
                        __html: processDescriptionHtml(holiday.description),
                      }}
                    />
                  )}
                </li>
              ))}
            </ul>
          </MDBModalBody>
        </MDBModalContent>
      </MDBModalDialog>
    </MDBModal>
  )
}

/**
 * HolidaysPage
 *
 * Main page component for displaying USABG holidays in a year-view calendar.
 * Allows users to view all company holidays for any given year.
 *
 * Features:
 * - Year-view calendar showing all 12 months
 * - Holiday highlighting on calendar dates
 * - List view of all holidays
 * - Year navigation (previous/next year)
 * - Click on holiday date for details
 *
 * @example
 * // Accessed via /holidays route
 * <Route path="/holidays" component={HolidaysPage} />
 */
const HolidaysPage = () => {
  const [modalOpen, setModalOpen] = useState(false)
  const [selectedDate, setSelectedDate] = useState(null)
  const [selectedHolidays, setSelectedHolidays] = useState([])

  /**
   * Initialize the holiday store on component mount.
   */
  useEffect(() => {
    HolidayStore.init()
    return () => HolidayStore.reset()
  }, [])

  /**
   * Handle date selection from the calendar.
   * Opens modal with holiday details.
   *
   * @param {string} date - Selected date
   * @param {Array} holidays - Holidays for the selected date
   */
  const handleDateSelect = (date, holidays) => {
    setSelectedDate(date)
    setSelectedHolidays(holidays)
    setModalOpen(true)
  }

  /**
   * Close the holiday detail modal.
   */
  const closeModal = () => {
    setModalOpen(false)
    setSelectedDate(null)
    setSelectedHolidays([])
  }

  return (
    <DashboardLayout>
      <main id='HolidaysPage' className='adminStyle mainSection pb-5'>
        <MDBContainer fluid className='mt-2'>
          <PageHeading label='USABG Holidays' />
          <MDBRow>
            <MDBCol size='12'>
              <YearCalendar onDateSelect={handleDateSelect} />
            </MDBCol>
          </MDBRow>
        </MDBContainer>
        <HolidayDetailModal isOpen={modalOpen} onClose={closeModal} date={selectedDate} holidays={selectedHolidays} />
      </main>
    </DashboardLayout>
  )
}

export default HolidaysPage
